"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GidApiClient = void 0;
const axios_1 = require("axios");
const http_1 = require("http");
const https_1 = require("https");
axios_1.default.defaults.httpAgent = new http_1.Agent({ keepAlive: false });
axios_1.default.defaults.httpsAgent = new https_1.Agent({ keepAlive: false });
class GidApiClient {
    constructor(config) {
        this.headers = {
            'content-type': 'application/json',
        };
        if (typeof config === 'string') {
            this.axios = axios_1.default.create({
                baseURL: config,
            });
        }
        else if (typeof config === 'undefined') {
            const baseURL = process.env.GID_API_SERVICE_PORT
                ? `http://gid-api:${process.env.GID_API_SERVICE_PORT}/v2`
                : process.env.API_URL;
            this.axios = axios_1.default.create({
                baseURL,
            });
        }
        else {
            this.axios = config;
        }
    }
    static getServiceUserHeaders(adminUserSfid, role = 'admin') {
        return {
            'gid-service-user': `${adminUserSfid}|${role}`,
        };
    }
    async request(url, method, data, headers) {
        return this.axios.request({
            url,
            method,
            headers: Object.assign(Object.assign({}, this.headers), headers),
            data: method === 'GET' ? null : data,
            params: method === 'GET' ? data : null,
            maxContentLength: 500 * 1024 * 1024,
            maxBodyLength: 500 * 1024 * 1024,
        });
    }
    async requestDataOnly(url, method, data, headers) {
        return this.request(url, method, data, headers).then((response) => response.data);
    }
    configure(accessToken, additionalHeaders = {}) {
        this.headers = Object.assign({ 'content-type': 'application/json' }, additionalHeaders);
        if (accessToken) {
            this.headers.authorization = `Bearer ${accessToken}`;
        }
        return this;
    }
    configureWithBrandServiceUser(adminUserSfid, additionalHeaders = {}) {
        this.headers = Object.assign(Object.assign({ 'content-type': 'application/json' }, GidApiClient.getServiceUserHeaders(adminUserSfid, 'brand')), additionalHeaders);
        return this;
    }
    configureWithAdminServiceUser(adminUserSfid, additionalHeaders = {}) {
        this.headers = Object.assign(Object.assign({ 'content-type': 'application/json' }, GidApiClient.getServiceUserHeaders(adminUserSfid)), additionalHeaders);
        return this;
    }
    login(params) {
        return this.requestDataOnly(`auth/login`, 'POST', params);
    }
    generateRefreshToken(params, refreshToken) {
        return this.requestDataOnly(`auth/refresh-token`, 'POST', params, refreshToken
            ? {
                Authorization: `Bearer ${refreshToken}`,
            }
            : {});
    }
    generateAccessToken(params, refreshToken) {
        return this.requestDataOnly(`auth/access-token`, 'POST', params, refreshToken
            ? {
                Authorization: `Bearer ${refreshToken}`,
            }
            : {});
    }
    generateReadonlyRefreshToken() {
        return this.requestDataOnly(`admin/refresh-token-readonly`, 'POST');
    }
    generateRefreshTokenFor(email, role, contact_sfid, lifetime, skip_log) {
        return this.requestDataOnly(`admin/refresh-token-for`, 'POST', {
            email,
            role,
            contact_sfid,
            lifetime,
            skip_log,
        });
    }
    accessTokenExpiration() {
        return this.requestDataOnly(`auth/expiration`, 'GET');
    }
    async service(serviceId) {
        return this.requestDataOnly(`brand/services/${serviceId}`, 'GET');
    }
    async services() {
        return this.requestDataOnly(`brand/services/all`, 'GET');
    }
    async servicesForJobForBrand(jobId) {
        return this.requestDataOnly(`brand/services/for-job/${jobId}`, 'GET');
    }
    async servicesForJobForAdmin(jobId) {
        return this.requestDataOnly(`admin/services/for-job/${jobId}`, 'GET');
    }
    async servicesForJobForPartner(jobId) {
        return this.requestDataOnly(`partner/services/for-job/${jobId}`, 'GET');
    }
    async servicesForJob(jobId, through = 'brand') {
        if (through === 'brand') {
            return await this.servicesForJobForBrand(jobId);
        }
        else if (through === 'admin') {
            return await this.servicesForJobForAdmin(jobId);
        }
        else if (through === 'partner') {
            return await this.servicesForJobForPartner(jobId);
        }
        else {
            throw new Error(`Services for job through ${through} is not implemented.`);
        }
    }
    async servicesOfJobForAdmin(jobId) {
        return this.requestDataOnly(`admin/services/of-job/${jobId}`, 'GET');
    }
    async servicesOfJobForPartner(jobId) {
        return this.requestDataOnly(`partner/services/of-job/${jobId}`, 'GET');
    }
    async servicesOfJob(jobId, through) {
        if (through === 'admin') {
            return await this.servicesOfJobForAdmin(jobId);
        }
        else if (through === 'partner') {
            return await this.servicesOfJobForPartner(jobId);
        }
        else {
            throw new Error(`Services of job through ${through} is not implemented.`);
        }
    }
    async servicesForOpportunityForBrand(opportunityId) {
        return this.requestDataOnly(`brand/opportunities/${opportunityId}/services`, 'GET');
    }
    async servicesForOpportunityForAdmin(opportunityId) {
        return this.requestDataOnly(`admin/opportunities/${opportunityId}/services`, 'GET');
    }
    async servicesForOpportunity(opportunityId, through) {
        if (through === 'admin') {
            return await this.servicesForOpportunityForAdmin(opportunityId);
        }
        else if (through === 'brand') {
            return await this.servicesForOpportunityForBrand(opportunityId);
        }
        else {
            throw new Error(`Services for opportunity through ${through} is not implemented.`);
        }
    }
    async productsOfJobForAdmin(jobId) {
        return this.requestDataOnly(`admin/products/of-job/${jobId}`, 'GET');
    }
    async productsOfJobForPartner(jobId) {
        return this.requestDataOnly(`partner/products/of-job/${jobId}`, 'GET');
    }
    async productsOfJob(jobId, through) {
        if (through === 'admin') {
            return await this.productsOfJobForAdmin(jobId);
        }
        else if (through === 'partner') {
            return await this.productsOfJobForPartner(jobId);
        }
        else {
            throw new Error(`Products of job through ${through} is not implemented.`);
        }
    }
    async product(productId) {
        return this.requestDataOnly(`brand/products/${productId}`, 'GET');
    }
    async products() {
        return this.requestDataOnly(`brand/products`, 'GET');
    }
    async productsForOpportunityForBrand(opportunityId) {
        return this.requestDataOnly(`brand/products/opportunity/${opportunityId}`, 'GET');
    }
    async productsForOpportunityForAdmin(opportunityId) {
        return this.requestDataOnly(`admin/products/opportunity/${opportunityId}`, 'GET');
    }
    async productsForOpportunity(opportunityId, through) {
        if (through === 'admin') {
            return await this.productsForOpportunityForAdmin(opportunityId);
        }
        else if (through === 'brand') {
            return await this.productsForOpportunityForBrand(opportunityId);
        }
        else {
            throw new Error(`Products for opportunity through ${through} is not implemented.`);
        }
    }
    async createFollowUpOrder(orderData, through) {
        if (through === 'partner') {
            return await this.createFollowUpOrderForPartner(orderData);
        }
        else if (through === 'admin') {
            return await this.createFollowUpOrderForAdmin(orderData);
        }
        else {
            throw new Error(`Follow-up order creation through ${through} is not implemented.`);
        }
    }
    async createFollowUpOrderForPartner(orderData) {
        return this.requestDataOnly(`partner/orders/create`, 'PUT', orderData);
    }
    async createFollowUpOrderForAdmin(orderData) {
        return this.requestDataOnly(`admin/jobs/follow-up/create`, 'PUT', orderData);
    }
    async createOrder(orderData, through = 'brand', lax_validation = 'no') {
        if (through === 'brand') {
            return await this.createOrderForBrand(orderData, lax_validation);
        }
        else if (through === 'admin') {
            return await this.createOrderForAdmin(orderData, lax_validation);
        }
        else {
            throw new Error(`Order creation through ${through} is not implemented.`);
        }
    }
    async createOrderForBrand(orderData, lax_validation) {
        return this.requestDataOnly(`brand/orders/create`, 'PUT', orderData);
    }
    async createOrderForAdmin(orderData, lax_validation) {
        let url = `admin/jobs/create`;
        if (['1', 'true', 'yes', '0', 'false', 'no'].includes(lax_validation)) {
            url = url.concat(`?lax_validation=${lax_validation}`);
        }
        return this.requestDataOnly(url, 'PUT', orderData);
    }
    async modifyOrder(orderData, through) {
        if (through === 'partner') {
            return await this.modifyOrderForPartner(orderData);
        }
        else if (through === 'admin') {
            return await this.modifyOrderForAdmin(orderData);
        }
        else if (through === 'customer') {
            return await this.modifyOrderForCustomer(orderData);
        }
        else {
            throw new Error(`Order modification through ${through} is not implemented.`);
        }
    }
    async modifyOrderForAdmin(orderData) {
        return this.requestDataOnly(`admin/jobs/set-status`, 'POST', orderData);
    }
    async modifyOrderForPartner(orderData) {
        return this.requestDataOnly(`partner/orders/set-status`, 'POST', orderData);
    }
    async modifyOrderForCustomer(orderData) {
        return this.requestDataOnly(`customer/orders/set-status`, 'POST', orderData);
    }
    async modifyOrderForBrand(orderData) {
        return this.requestDataOnly(`brand/orders/set-status`, 'POST', orderData);
    }
    async order(orderID, through = 'brand') {
        if (through === 'brand') {
            return await this.orderForBrand(orderID);
        }
        else if (through === 'admin') {
            return await this.orderForAdmin(orderID);
        }
        else if (through === 'partner') {
            return await this.orderForPartner(orderID);
        }
        else {
            throw new Error(`Order fetch through ${through} is not implemented.`);
        }
    }
    async orderForBrand(orderID) {
        return this.requestDataOnly(`brand/orders/${orderID}`, 'GET');
    }
    async orderForAdmin(orderID) {
        return this.requestDataOnly(`admin/jobs/${orderID}`, 'GET');
    }
    async orderForPartner(orderID) {
        return this.requestDataOnly(`partner/orders/${orderID}`, 'GET');
    }
    async orders(searchOptions) {
        return this.requestDataOnly(`brand/orders`, 'GET', searchOptions);
    }
    async orderStatuses() {
        return this.requestDataOnly(`brand/orders/statuses`, 'GET');
    }
    async sendEmail(emailData) {
        return this.requestDataOnly(`admin/send-email`, 'POST', emailData);
    }
    async orderFlags(orderData) {
        return this.requestDataOnly(`admin/jobs/flags`, 'PUT', orderData);
    }
    async phoneVerification(number) {
        return this.requestDataOnly(`admin/phone/${number}`, 'GET');
    }
    async getBrandQuotePath(jobId) {
        return this.requestDataOnly(`admin/jobs/${jobId}/brand-quote`, 'GET');
    }
}
exports.GidApiClient = GidApiClient;
//# sourceMappingURL=client.js.map